import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, ToggleButton, ToggleButtonGroup, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';

import { reportingAPI } from '../services/api';

const PERIODS = { last_month: 'Last month', last_year: 'Last 12 months', all_time: 'All time' };
const SAMPLES = '#1976d2';
const TESTS = '#2e7d32';
const num = (n) => Number(n || 0).toLocaleString();

function Stat({ label, value, sub }) {
  return (
    <Card>
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" component="div">{value}</Typography>
        <Typography variant="caption" color="text.secondary">{sub}</Typography>
      </CardContent>
    </Card>
  );
}

// One customer's samples and tests as two bars scaled to the busiest customer shown.
function Bars({ rows }) {
  const top = Math.max(1, ...rows.map((r) => Number(r.total_orders || 0)), ...rows.map((r) => Number(r.total_tasks || 0)));
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(70px, auto) 1fr', columnGap: 1.5, rowGap: 1, alignItems: 'center' }} role="img" aria-label="Samples and tests for the busiest customers">
      {rows.map((r) => (
        <React.Fragment key={r.cust_code}>
          <Tooltip title={r.cust_name}><Typography variant="body2" sx={{ fontWeight: 500 }}>{r.cust_code}</Typography></Tooltip>
          <Box>
            {[['Samples', r.total_orders, SAMPLES], ['Tests', r.total_tasks, TESTS]].map(([label, value, color]) => (
              <Tooltip key={label} title={`${label}: ${num(value)}`} placement="top-start">
                <Box sx={{ height: 9, mb: '3px', borderRadius: 0.5, bgcolor: color, width: `${Math.max(0.5, (Number(value || 0) / top) * 100)}%` }} />
              </Tooltip>
            ))}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

export default function UsageOverview() {
  const [dimension, setDimension] = useState('last_year');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    reportingAPI.usage(dimension)
      .then((res) => live && setData(res.data))
      .catch((err) => live && setError(err.response?.data?.detail || 'Could not load usage'));
    return () => { live = false; };
  }, [dimension]);

  const customers = data?.customers || [];
  const samples = customers.reduce((s, c) => s + Number(c.total_orders || 0), 0);
  const tests = customers.reduce((s, c) => s + Number(c.total_tasks || 0), 0);
  const reporting = customers.filter((c) => Number(c.total_orders) > 0).length;
  const top = customers.slice(0, 8);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Usage Overview</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Samples and tests reported by each active customer's STARLIMS databases. Periods are whole months and end before the current month.
      </Typography>
      <ToggleButtonGroup exclusive size="small" value={dimension} onChange={(e, v) => v && setDimension(v)} aria-label="Period" sx={{ mb: 2 }}>
        {Object.entries(PERIODS).map(([k, label]) => <ToggleButton key={k} value={k}>{label}</ToggleButton>)}
      </ToggleButtonGroup>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {data && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Stat label="Total samples" value={num(samples)} sub="all active customers" />
            <Stat label="Total tests" value={num(tests)} sub="all active customers" />
            <Stat label="Reporting customers" value={num(reporting)} sub="with samples in the period" />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1.4fr' }, gap: 2, alignItems: 'start' }}>
            <Paper sx={{ p: 2, minWidth: 0 }}>
              <Typography variant="subtitle1" gutterBottom>Top 8 customers by samples</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography variant="caption"><Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: SAMPLES, mr: 0.5 }} />Samples</Typography>
                <Typography variant="caption"><Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: TESTS, mr: 0.5 }} />Tests</Typography>
              </Box>
              {top.length ? <Bars rows={top} /> : <Typography color="text.secondary">No usage in this period.</Typography>}
            </Paper>
            <Paper sx={{ minWidth: 0 }}>
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow><TableCell>Customer</TableCell><TableCell>Location</TableCell><TableCell align="right">Samples</TableCell><TableCell align="right">Tests</TableCell><TableCell align="right">Months</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((c) => (
                      <TableRow key={c.cust_code} hover>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{c.cust_name}</Typography><Typography variant="caption" color="text.secondary">{c.cust_code}</Typography></TableCell>
                        <TableCell>{c.location || '-'}</TableCell>
                        <TableCell align="right">{num(c.total_orders)}</TableCell>
                        <TableCell align="right">{num(c.total_tasks)}</TableCell>
                        <TableCell align="right">{c.months_reported}</TableCell>
                      </TableRow>
                    ))}
                    {customers.length === 0 && <TableRow><TableCell colSpan={5}>No active customers.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
}
